// workflow-ui.ts — pure helpers shared by API + Web for rendering workflow
// state and categorizing timeline events. No DB, no HTTP. Importing this
// module costs nothing at runtime.

import type { EventType, RequestRecord, Status } from './types.js';

// ── Event categories ───────────────────────────────────────────────────────
//
// Every EventType maps to exactly one category. Categories drive icon,
// color, and visual weight in the timeline + audit log. Adding a new
// EventType MUST also add a category mapping below — TypeScript will not
// catch a missing entry (Record<EventType,...> would, but the mapping is
// switch-based for forward-compat with EventType arrays).

export const EVENT_CATEGORIES = [
  'submission',
  'reviewer_action',
  'clarification_requested',
  'clarification_responded',
  'workflow_transition',
  'ai_generation',
  'audit_meta',
] as const;
export type EventCategory = (typeof EVENT_CATEGORIES)[number];

export function eventCategory(eventType: EventType): EventCategory {
  switch (eventType) {
    case 'submitted':
      return 'submission';
    case 'it_approved':
    case 'it_rejected':
    case 'legal_approved':
    case 'legal_approved_with_conditions':
    case 'legal_rejected':
      return 'reviewer_action';
    case 'it_clarification_requested':
    case 'legal_clarification_requested':
      return 'clarification_requested';
    case 'submitter_responded':
      return 'clarification_responded';
    case 'vendor_intel_regenerated':
      return 'ai_generation';
    case 'admin_edited':
    case 'vendor_reviewer_note_changed':
    case 'feature_flag_changed':
    case 'notification_template_changed':
      return 'audit_meta';
  }
}

export type ActorKind = 'human' | 'system' | 'ai';

export function actorKindFor(category: EventCategory): ActorKind {
  if (category === 'ai_generation') return 'ai';
  if (category === 'audit_meta') return 'system';
  return 'human';
}

// ── Workflow state derivation ──────────────────────────────────────────────
//
// Today the StageTracker computed itself directly from `status`, which
// caused "IT Needs Clarification" to leak onto the Decision (stage 4)
// circle whenever a reviewer requested clarification. The correct mental
// model is: when a reviewer requests clarification, ownership transfers
// back to the submitter and the active stage is "blocked, waiting on
// requester". Stage 4 should remain neutral until a final decision lands.

export interface WorkflowState {
  /** 1 = Submitted, 2 = IT Review, 3 = Legal Review, 4 = Decision. */
  stage: 1 | 2 | 3 | 4;
  ownership: 'submitter' | 'it' | 'legal' | 'closed';
  blocked: boolean;
  blockedReason: 'awaiting_submitter_it' | 'awaiting_submitter_legal' | null;
  /** Short human label for the owner — e.g., "John Dean (submitter)" or "IT reviewer". */
  ownerLabel: string;
}

function stageForStatus(status: Status): 2 | 3 | 4 {
  switch (status) {
    case 'pending_it_review':
    case 'it_clarification_requested':
      return 2;
    case 'pending_legal_review':
    case 'legal_clarification_requested':
      return 3;
    case 'approved':
    case 'approved_with_conditions':
    case 'rejected':
      return 4;
  }
}

export function deriveWorkflowState(
  request: Pick<RequestRecord, 'status' | 'submitterName'>,
): WorkflowState {
  const stage = stageForStatus(request.status);

  if (
    request.status === 'approved' ||
    request.status === 'approved_with_conditions' ||
    request.status === 'rejected'
  ) {
    return {
      stage: 4,
      ownership: 'closed',
      blocked: false,
      blockedReason: null,
      ownerLabel: request.status === 'rejected' ? 'Closed (rejected)' : 'Closed (approved)',
    };
  }

  if (request.status === 'it_clarification_requested') {
    return {
      stage: 2,
      ownership: 'submitter',
      blocked: true,
      blockedReason: 'awaiting_submitter_it',
      ownerLabel: `Awaiting ${request.submitterName} (submitter)`,
    };
  }

  if (request.status === 'legal_clarification_requested') {
    return {
      stage: 3,
      ownership: 'submitter',
      blocked: true,
      blockedReason: 'awaiting_submitter_legal',
      ownerLabel: `Awaiting ${request.submitterName} (submitter)`,
    };
  }

  if (request.status === 'pending_it_review') {
    return {
      stage: 2,
      ownership: 'it',
      blocked: false,
      blockedReason: null,
      ownerLabel: 'IT reviewer',
    };
  }

  // pending_legal_review
  return {
    stage: 3,
    ownership: 'legal',
    blocked: false,
    blockedReason: null,
    ownerLabel: 'Legal reviewer',
  };
}

// Short verb form used in the timeline event row. Kept separate from event
// CATEGORY because two events of the same category can read differently
// (e.g., it_approved vs legal_approved are both reviewer_action but render
// as "approved at IT review" vs "approved at Legal review").
export function eventVerb(eventType: EventType): string {
  switch (eventType) {
    case 'submitted':
      return 'submitted the request';
    case 'it_approved':
      return 'approved at IT review';
    case 'it_rejected':
      return 'rejected at IT review';
    case 'it_clarification_requested':
      return 'requested clarification (IT)';
    case 'legal_approved':
      return 'approved at Legal review';
    case 'legal_approved_with_conditions':
      return 'approved with conditions';
    case 'legal_rejected':
      return 'rejected at Legal review';
    case 'legal_clarification_requested':
      return 'requested clarification (Legal)';
    case 'submitter_responded':
      return 'responded to clarification';
    case 'admin_edited':
      return 'edited submitted info (admin)';
    case 'vendor_intel_regenerated':
      return 'regenerated vendor intel';
    case 'vendor_reviewer_note_changed':
      return 'updated reviewer note';
    case 'feature_flag_changed':
      return 'changed a feature flag';
    case 'notification_template_changed':
      return 'edited a notification template';
  }
}
